import { type FC, useEffect } from "react";
import { useBoolean } from "usehooks-ts";

import {
  useAssessedCountDataWithNotSubmitted,
  useAssessmentMode,
  useAssessmentNavData,
  useFirstAssessableResponseId,
} from "../../../../utils/assessment";
import { useResponsesForPage } from "../responses";

import { AssessmentExitDialog } from "./AssessmentExitDialog";

import { getOrRunAiAssessment } from "components/ai/queries/assessment-cache";
import { ACCESS_ID_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { AssessmentGradingForm } from "components/materials/page/assessment/AssessmentGradingForm";
import { AssessmentGradingLastPanel } from "components/materials/page/assessment/AssessmentGradingLastPanel";
import { AssessmentGradingNoSubmission } from "components/materials/page/assessment/AssessmentGradingNoSubmission";
import { ContentSidebarHeader } from "components/materials/sidebar/ContentSidebarHeader";
import { ContentSidebarMain } from "components/materials/sidebar/ContentSidebarMain";
import { OverlayContainer } from "components/materials/sidebar/OverlayContainer";
import { t } from "i18n/i18n";
import { CloseButton } from "mds/components/CloseButton";
import { useKeyPress } from "mds/hooks/use-keyboard";
import { AssessPointsMarkerIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  pageSelectors,
  selectFullSubmissionById,
  selectIsAssessWithAIModeEnabled,
  selectShouldShowModalOnExitAssessmentMode,
} from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { useQueryParam, useQueryParamsActions } from "utils/routing";

interface AssessmentGradingViewProps {
  pageId: string;
}

export const AssessmentGradingView: FC<AssessmentGradingViewProps> = ({ pageId }) => {
  const { setQueryParams } = useQueryParamsActions();
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["assessments_published_at"],
  );
  const assessedCountData = useAssessedCountDataWithNotSubmitted(pageId);
  const shouldShowModalOnExitAssessmentMode = useAppSelector(
    selectShouldShowModalOnExitAssessmentMode,
  );
  const { stopAssessingAndMaybeShowAssessmentSummary } = useAssessmentMode(pageId);

  const {
    value: exitDialogIsOpen,
    setTrue: openExitDialog,
    setFalse: closeExitDialog,
  } = useBoolean(false);

  const openExitDialogOrExitAssessmentMode = () => {
    if (shouldShowModalOnExitAssessmentMode) {
      openExitDialog();
    } else {
      stopAssessingAndMaybeShowAssessmentSummary();
    }
  };

  const { responses } = useResponsesForPage(pageId);
  const firstId = useFirstAssessableResponseId(pageId, responses);
  const { nextId, prevId, submissionForNextUser, onBack, onNext, isLastPanel } =
    useAssessmentNavData(pageId);

  const accessParamId = useQueryParam(ACCESS_ID_QUERY_PARAM);
  const submissionId = useQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const submission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));

  useKeyPress({
    shiftKeys: ["ArrowLeft", "ArrowRight"],
    shiftCallback: (key) => {
      if (isLastPanel) {
        if (key === "ArrowLeft") onBack();
        return;
      }

      const submitNavigation = (source: "next" | "prev") => {
        const form = document.getElementById("assessment-grading-form");
        const submitButton = document.querySelector<HTMLButtonElement>(
          `button[name="submitSource"][value="${source}"]`,
        );
        if (form instanceof HTMLFormElement && submitButton) {
          form.requestSubmit(submitButton);
        }
      };

      if (key === "ArrowRight") {
        if (submission) submitNavigation("next");
        else onNext();
      } else if (prevId) {
        if (submission) submitNavigation("prev");
        else onBack();
      }
    },
  });

  // Ensures AI assessments for the current and next submissions are pre-fetched
  // TODO: Eventually, we could make this pre-fetch assessments for every submission for the current page
  const isAssessWithAIModeEnabled = useAppSelector(selectIsAssessWithAIModeEnabled);
  const nextSubmissionId = submissionForNextUser?.id;
  const nextSubmission = useAppSelector((s) => selectFullSubmissionById(s, nextSubmissionId));

  useEffect(() => {
    if (isAssessWithAIModeEnabled && submission && !isLastPanel) {
      getOrRunAiAssessment(submission);
    }
  }, [isAssessWithAIModeEnabled, submission, isLastPanel]);

  useEffect(() => {
    if (isAssessWithAIModeEnabled && nextSubmission && !isLastPanel) {
      getOrRunAiAssessment(nextSubmission);
    }
  }, [isAssessWithAIModeEnabled, nextSubmission, isLastPanel]);

  useEffect(() => {
    if (!accessParamId && firstId) {
      // Use replace so the initial "missing user param" URL doesn't become a dead-end
      // history entry that immediately forwards when the user hits Back.
      setQueryParams({ [ACCESS_ID_QUERY_PARAM]: firstId });
    }
  }, [accessParamId, firstId, setQueryParams]);

  // There are three different views in the grading view: the grading form, the no submission view, and the last panel.
  return (
    <OverlayContainer className="bg-white lg:max-w-[400px]">
      <ContentSidebarHeader panelType="overlay" visibleOnMobile>
        <div className="flex w-full flex-row items-center justify-between">
          <h3 className="flex flex-row items-center gap-2">
            <AssessPointsMarkerIcon className="text-green" />
            {t("glossary.assessment")}
          </h3>

          <div className="flex items-center">
            <CloseButton
              aria-label={t("assessment.stop_assessing")}
              eventName="Assessment - Stop"
              onClick={openExitDialogOrExitAssessmentMode}
            />
          </div>
        </div>
      </ContentSidebarHeader>

      {isLastPanel ? (
        <ContentSidebarMain bottomPadding={false}>
          <div className="mt-2 flex flex-col gap-4">
            <AssessmentGradingLastPanel
              assessedCountData={assessedCountData}
              hideButtons={!!page?.assessments_published_at}
              pageId={pageId}
              onBack={onBack}
            />
          </div>
        </ContentSidebarMain>
      ) : !submission ? (
        <ContentSidebarMain bottomPadding={false}>
          <div className="mt-2 flex flex-col gap-4">
            <AssessmentGradingNoSubmission />
          </div>
        </ContentSidebarMain>
      ) : (
        <AssessmentGradingForm
          key={submissionId ?? "no-submission"}
          nextId={nextId}
          pageId={pageId}
          prevId={prevId}
        />
      )}

      <AssessmentExitDialog
        count={assessedCountData.count}
        open={exitDialogIsOpen}
        published={!!page?.assessments_published_at}
        total={assessedCountData.total}
        onClose={closeExitDialog}
        onConfirmExit={stopAssessingAndMaybeShowAssessmentSummary}
      />
    </OverlayContainer>
  );
};
